import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import _mapValues from 'lodash/mapValues';
import moment from 'moment';
import qs from 'qs';
import cloneDeep from 'lodash/cloneDeep';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import omit from 'lodash/omit';
import forEach from 'lodash/forEach';
import orderBy from 'lodash/orderBy';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import createCalendar, { getDateRangeFromCalendar } from '../../shared/calendar';
import { createSelectOptionsFromSysEnums } from '../../shared/form-elements/select-countries';

const isRocket = process.env.CPA_ENV === 'rocket';

const calendar = createCalendar({
	namespace: 'programsInfo',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// actions types
const TOGGLE_PROGRESS_BAR = 'programsInfo/toggle-progress-bar';
const TAB_CHANGE = 'programsInfo/tab-change';

const FETCH_SUGGEST_SUCCESS = 'programsInfo/FETCH_SUGGEST_SUCCESS';
const FETCH_SUGGEST_ERROR = 'programsInfo/FETCH_SUGGEST_ERROR';

const FETCH_OFFER_SUCCESS = 'programsInfo/fetch-offer-success';
const FETCH_ANALYTICS_SUCCESS = 'programsInfo/fetch-analytics-success';
const FETCH_ALLPROMOS_SUCCESS = 'programsInfo/fetch-allpromos-success';

const FETCH_ERROR = 'programsInfo/fetch-error';

// permissions
export const allowedPermissions = {
	'view': 'offers_list'
};

// initial state
const initialState = {
	data: {},
	analytics: null,
	promos: null,
	calendar: calendar.initialState,
	showProgressBar: false,
	currentTab: 'analytics',
	error: false
};

// actions
export const actions = Object.assign({
	tabChange: tabIndex => (dispatch, getState) => {
		const { offer } = getState().programsInfo;

		const offerId = parseInt(last(window.location.pathname.split('/')));

		if (tabIndex === 'analytics') {
			if (offerId && isEmpty(offer)) {
				dispatch(actions.toggleProgressBar());
				dispatch(actions.fetchOfferData(offerId)).then(action => {
					if (action.type === FETCH_OFFER_SUCCESS) {
						dispatch(actions.fetchAnalytics(action.payload.data.id));
					}
				});
			}
		}

		if (tabIndex === 'promos') {
			if (offerId) {
				dispatch(actions.toggleProgressBar());
				dispatch(actions.fetchAllPromos(offerId));
			}
		}

		dispatch({
			type: TAB_CHANGE,
			payload: tabIndex
		});
	},

	fetchOfferData: id => dispatch => {
		return http({
			url: `${endpoints.OFFERS.BASE}/${id}`,
			method: 'get',
			actions: [
				FETCH_OFFER_SUCCESS,
				FETCH_ERROR
			]
		})(dispatch);
	},

	fetchAnalytics: id => (dispatch, getState) => {
		const { calendar } = getState().programsInfo;

		const analyticsFiltersFormData = selectors.getAnalyticsFiltersFormData(getState());

		const query = qs.stringify({
			date_range: getDateRangeFromCalendar(calendar),
			filters: _mapValues(analyticsFiltersFormData, i => i && i.map(val => val.value))
		});

		dispatch(actions.toggleProgressBar());

		return http({
			url: `${endpoints.OFFERS.BASE}/${id}/analytics?${query}`,
			method: 'get',
			actions: [FETCH_ANALYTICS_SUCCESS, FETCH_ERROR]
		})(dispatch);
	},
	fetchAllPromos: id => (dispatch, getState) => {
		const values = getFormValues('promosFiltersForm')(getState());

		const countries = values && values.countries.map((c) => c.value);

		return http({
			url: endpoints.OFFERS.LANDINGS.replace('{id}', id),
			method: 'get',
			actions: [FETCH_ALLPROMOS_SUCCESS, FETCH_ERROR],
			query: countries && {
				filters: {
					country_ids: countries
				}
			}
		})(dispatch);
	},
	locationsLookups: text => (dispatch, getState) => {
		const request = http({
			url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
			method: 'get',
			actions: [FETCH_SUGGEST_SUCCESS, FETCH_SUGGEST_ERROR]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_SUGGEST_SUCCESS) {
				return {
					options: createSelectOptionsFromSysEnums( action.payload.data )
				};
			}
		});
	},

	applyAnalytics: () => (dispatch, getState) => {
		const { offer } = getState().programsInfo;
		dispatch(actions.fetchAnalytics(offer.id));
	},
	applyAllPromos: () => (dispatch, getState) => {
		const { offer } = getState().programsInfo;
		dispatch(actions.fetchAllPromos(offer.id));
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	})
},
calendar.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: (state, action) => {
		if (action.payload.pathname.indexOf('programs/info') === -1) {
			return initialState;
		}
		return state;
	},
	[FETCH_OFFER_SUCCESS]: (state, action) => ({
		...state,
		data: action.payload.data,
		offer: action.payload.data,
		showProgressBar: false
	}),
	[FETCH_ANALYTICS_SUCCESS]: (state, action) => {
		const data = action.payload.data;

		const fieldsToProcess = data[0] && omit(Object.keys(data[0]), ['country']) || [];

		const preparedData = reduce(data, (res, row) => {
			forEach(fieldsToProcess, f => {
				res[f] = (res[f] || 0) + Number(row[f]);
				if (!isRocket) {
					res.cnt_ages = (res.cnt_ages || 0)
						+ Number(row.age_0_19) + Number(row.age_20_24) + Number(row.age_25_31) + Number(row.age_32_36)
						+ Number(row.age_37_39) + Number(row.age_40_44) + Number(row.age_45_51) + Number(row.age_52_54)
						+ Number(row.age_55_59) + Number(row.age_60_64) + Number(row.age_65_69) + Number(row.age_70_74)
						+ Number(row.age_75_81) + Number(row.age_82);
				} else {
					res.cnt_ages = (res.cnt_ages || 0)
						+ Number(row.age_0_25) + Number(row.age_26_35) + Number(row.age_36_45) + Number(row.age_46_55)
						+ Number(row.age_56_65) + Number(row.age_66_75) + Number(row.age_76);
				}
			});
			res.showEmptyAgeChart = !res.cnt_ages;
			if (!row.country) {
				row.country = 'Unknown';
			}
			const country = row.country.replace(/\(.*\)/, '').trim();
			res.countries.push({
				country,
				cnt: parseInt(row.total_conversions),
				approval_percent: Math.round((parseInt(row.accepted_conversions) / parseInt(row.total_conversions)) * 100)
			});
			return res;
		}, {countries: [], showEmptyAgeChart: true});

		preparedData.countries = orderBy(preparedData.countries, ['cnt'], ['desc']);

		return {
			...state,
			analytics: preparedData,
			showProgressBar: false
		};
	},
	[FETCH_ALLPROMOS_SUCCESS]: (state, action) => {
		const promos = action.payload.data;

		return {
			...state,
			promos: {
				landings: promos.filter((p) => p.type === 'landing').map((l) => l.domain),
				transits: promos.filter((p) => p.type === 'transit').map((l) => l.domain)
			},
			showProgressBar: false
		};
	},
	[FETCH_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[TAB_CHANGE]: (state, action) => ({...state, currentTab: action.payload})
},
calendar.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.programsInfo.data,
	getAnalytics: state => state.programsInfo.analytics,
	getPromos: state => state.programsInfo.promos,
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.programsInfo.calendar,
		state => state
	)(state),
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getCurrentTab: state => state.programsInfo.currentTab,
	getAnalyticsFiltersFormData: state => getFormValues('analyticsFiltersForm')(state),
	getTrafficSources: state => createSelectOptionsFromSysEnums(state.root.lists['traffic-sources']),
	getError: state => state.programsInfo.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/info/index.js